import { useState } from 'react';
import { isoWeekday } from '../domain/positions';
import {
  ddmmaaaa, mesDelCalendario, mesInicial, moverMes, ubicacionDelMes, type MesVisto,
} from '../domain/calendario';

function mesDe(fecha: string): MesVisto {
  const [anio, mes] = fecha.split('-').map(Number);
  return { anio, mes };
}

const CABECERA = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const NOMBRE_DIA = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];

/**
 * Fechas sueltas de un horario, tildadas sobre el calendario de la campaña (spec 2026-09-25).
 *
 * Un mes a la vez, con flechas: se puede pasar a meses fuera de la campaña, pero ahí los días van
 * deshabilitados y un aviso dice qué fecha de la campaña ampliar. No se puede elegir fuera porque
 * el servidor no acepta jornadas fuera del rango de la campaña (`insert_positions`), y de ese
 * rango dependen la planificación y el cierre de pago: ampliarlo es una decisión aparte.
 *
 * Tres estados y ninguno depende solo del color (CLAUDE.md): la elegida va RELLENA y con
 * `aria-pressed`; la que ya cubre el patrón semanal lleva un punto y lo dice en su etiqueta; la que
 * cae fuera de la campaña va deshabilitada. Tocar una que ya cubre el patrón es inofensivo:
 * `expandSchedule` recorre por fecha y no la duplica.
 */
export default function CalendarioFechasSueltas({ desde, hasta, weekdays, seleccionadas, onToggle }: {
  desde: string;
  hasta: string;
  weekdays: number[];
  seleccionadas: string[];
  onToggle: (fecha: string) => void;
}) {
  const [visto, setVisto] = useState(() => mesInicial(desde, hasta, seleccionadas));
  const m = mesDelCalendario(visto.anio, visto.mes);
  const ubicacion = ubicacionDelMes(visto, desde, hasta);
  const elegidas = new Set(seleccionadas);
  const patron = new Set(weekdays);
  // El mes va en el nombre de cada día: sin él, un lector de pantalla no distingue el «miércoles 16»
  // de un mes del de otro.
  const deMes = `de ${m.titulo.split(' ')[0].toLowerCase()} de ${m.anio}`;

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <button type="button" aria-label="Mes anterior" onClick={() => setVisto((v) => moverMes(v, -1))}
          className="rounded px-2 py-1 text-gray-700 hover:bg-gray-100">◀</button>
        <p className="text-xs font-semibold text-gray-700">{m.titulo}</p>
        <button type="button" aria-label="Mes siguiente" onClick={() => setVisto((v) => moverMes(v, 1))}
          className="rounded px-2 py-1 text-gray-700 hover:bg-gray-100">▶</button>
      </div>
      {ubicacion !== 'dentro' && (
        <p className="mb-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
          Este mes está fuera de la campaña, que va del {ddmmaaaa(desde)} al {ddmmaaaa(hasta)}. Para
          programarlo, amplía la {ubicacion === 'antes' ? 'fecha de inicio' : 'fecha fin'} en la
          pestaña «Datos».
        </p>
      )}
      <div role="group" aria-label={m.titulo} className="grid grid-cols-7 gap-1 text-center">
        {CABECERA.map((c) => (
          <span key={c} className="text-[10px] font-semibold text-gray-500">{c}</span>
        ))}
        {m.semanas.flat().map((f, i) => {
          if (f === null) return <span key={`vacio-${i}`} />;
          const dia = Number(f.slice(8));
          const fuera = f < desde || f > hasta;
          const elegida = elegidas.has(f);
          const enPatron = !fuera && patron.has(isoWeekday(f));
          const estado = fuera ? 'fuera de la campaña'
            : elegida ? 'fecha suelta elegida'
              : enPatron ? 'ya incluido por los días de la semana' : '';
          const clases = fuera ? 'text-gray-300 cursor-default'
            : elegida ? 'bg-blue-600 text-white font-semibold'
              : enPatron ? 'bg-blue-50 text-blue-900 hover:bg-blue-100'
                : 'text-gray-800 hover:bg-gray-100';
          return (
            <button
              key={f}
              type="button"
              disabled={fuera}
              aria-pressed={fuera ? undefined : elegida}
              aria-label={`${NOMBRE_DIA[isoWeekday(f) - 1]} ${dia} ${deMes}${estado ? ` — ${estado}` : ''}`}
              onClick={() => onToggle(f)}
              className={`flex h-9 flex-col items-center justify-center rounded text-xs ${clases}`}
            >
              <span>{dia}</span>
              {enPatron && !elegida && (
                <span aria-hidden className="text-[10px] leading-none">•</span>
              )}
            </button>
          );
        })}
      </div>
      <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
        <span>
          <span className="mr-1 inline-block h-3 w-3 rounded bg-blue-600 align-middle" />
          Fecha suelta elegida
        </span>
        <span><span className="mr-1 font-bold">•</span>Ya incluido por los días de la semana</span>
        <span className="text-gray-400">Gris: fuera de la campaña</span>
      </p>
      {/* Con un mes a la vez, una fecha elegida en otro mes no se vería al editar: se listan todas,
          y cada una lleva a su mes. */}
      <p className="mt-1 text-xs text-gray-600">
        {seleccionadas.length === 0 ? 'Ninguna fecha suelta elegida.' : (
          <>
            {seleccionadas.length}{' '}
            {seleccionadas.length === 1 ? 'fecha suelta elegida' : 'fechas sueltas elegidas'}:{' '}
            {[...seleccionadas].sort().map((f) => (
              <button key={f} type="button" aria-label={`Ir al mes de ${ddmmaaaa(f)}`}
                onClick={() => setVisto(mesDe(f))}
                className="mr-2 text-blue-700 underline">
                {ddmmaaaa(f)}
              </button>
            ))}
          </>
        )}
      </p>
    </div>
  );
}
